import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdLocationOn, MdEdit, MdDelete, MdAdd, MdClose, MdSave, MdFileDownload, MdUpload,
  MdAccountTree, MdViewList, MdChevronRight, MdExpandMore,
} from 'react-icons/md';
import api from '../api/axios';
import { useChangeStream } from '../lib/useChangeStream';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import SearchableSelect from '../components/SearchableSelect';

const Toast = ({ msg, type, onClose }) => (
  // Defensive sizing + fallback text — see AllAssets.js for the
  // history of the white-on-white CSS bug that this layout works
  // around. Keep all toasts in the app shaped like this until we
  // extract a shared component.
  <div className={`toast toast-${type}`} style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    minWidth: 280, maxWidth: 480, padding: '12px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 12,
    fontSize: 14, lineHeight: 1.4,
  }}>
    <div style={{ flex: 1 }}>{msg || '(no message — see browser console)'}</div>
    <button onClick={onClose} style={{
      background: 'none', border: 'none', color: 'inherit',
      cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0,
    }} aria-label="Dismiss">×</button>
  </div>
);

// Styled replacement for window.confirm. See AllAssets.js for full
// docs — the gist is two action buttons, an explicit X / overlay
// dismiss path, and an optional confirmStyle:'danger' that reddens
// the primary button. If the caller distinguishes Cancel-the-button
// from clicked-outside (e.g. import-confirm where Cancel = "skip
// duplicates" is itself an action), pass onDismiss.
const ConfirmModal = ({
  open, title, message, confirmLabel = 'OK', cancelLabel = 'Cancel',
  confirmStyle = 'primary', onConfirm, onCancel, onDismiss,
}) => {
  if (!open) return null;
  const confirmBg = confirmStyle === 'danger' ? 'var(--danger, #d32f2f)' : 'var(--primary, #1a3c6e)';
  const dismiss = onDismiss || onCancel;
  return (
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9998, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 14, maxWidth: 460, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
      }}>
        <div style={{ padding: '18px 22px 6px', borderBottom: '1px solid #e0e6ef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#1a3c6e' }}>{title}</h3>
          <button onClick={dismiss} aria-label="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#90a4ae', fontSize: 22, lineHeight: 1, padding: 0,
          }}>×</button>
        </div>
        <div style={{ padding: '14px 22px 18px', fontSize: 14, color: '#37474f', lineHeight: 1.55, whiteSpace: 'pre-line' }}>
          {message}
        </div>
        <div style={{ padding: '12px 18px', background: '#f8fafc', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1',
            background: 'white', color: '#37474f', cursor: 'pointer', fontSize: 14, fontWeight: 500,
          }}>{cancelLabel}</button>
          <button onClick={onConfirm} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: confirmBg, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const LocationMaster = () => {
  const nav = useNavigate();
  const [locations, setLocations] = useState([]);
  const [tree, setTree] = useState([]);
  const [view, setView] = useState('flat'); // flat | tree
  const [form, setForm] = useState({ location_code: '', location_name: '', description: '', parent_id: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Pending file upload — held while we ask the operator about
  // duplicates. See AllAssets for the same pattern.
  const [pendingImport, setPendingImport] = useState(null);
  // Generic confirm modal: { title, message, confirmLabel, confirmStyle, onConfirm }
  const [confirmModal, setConfirmModal] = useState(null);
  const importRef = useRef();

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.size === locations.length ? new Set() : new Set(locations.map(l => l.id)));
  };
  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    setConfirmModal({
      title: 'Delete selected locations?',
      message:
        `You're about to delete ${selectedIds.size} location(s).\n\n` +
        `Locations with assets or sub-locations will be skipped automatically — only empty leaves are removed.`,
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const r = await api.post('/locations/bulk-delete', { ids: Array.from(selectedIds) });
          const { deleted = 0, skipped = [] } = r.data || {};
          showToast(
            `Deleted ${deleted}` + (skipped.length ? ` · ${skipped.length} skipped (in use)` : ''),
            skipped.length && !deleted ? 'error' : 'success',
          );
          setSelectedIds(new Set());
          fetchLocations();
        } catch (err) {
          showToast(err.response?.data?.error || 'Bulk delete failed', 'error');
        }
      },
    });
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchLocations = async () => {
    const [flat, t] = await Promise.all([
      api.get('/locations'),
      api.get('/locations/tree'),
    ]);
    setLocations(flat.data);
    setTree(t.data);
  };

  useEffect(() => { fetchLocations(); }, []);
  useChangeStream(() => { fetchLocations(); }, ['locations']);

  const validate = (f) => {
    const errs = {};
    if (!f.location_code.trim()) errs.location_code = 'Code required';
    if (!f.location_name.trim()) errs.location_name = 'Name required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.post('/locations', { ...form, parent_id: form.parent_id || null });
      showToast('Location added successfully');
      setForm({ location_code: '', location_name: '', description: '', parent_id: '' });
      setErrors({});
      fetchLocations();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to add location', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    const errs = validate(editModal);
    if (Object.keys(errs).length > 0) return;
    try {
      await api.put(`/locations/${editModal.id}`, editModal);
      showToast('Location updated');
      setEditModal(null);
      fetchLocations();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/locations/${deleteModal.id}`);
      showToast('Location deleted');
      setDeleteModal(null);
      fetchLocations();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleExport = async () => {
    try {
      await downloadFromApi('/locations/export/excel', tsFilename('locations', 'xlsx'));
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  // Two-stage import. The file picker stashes into pendingImport; the
  // styled ConfirmModal asks about duplicates; runImport actually uploads
  // after the user picks Update / Skip / X-to-cancel.
  const handleImport = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setPendingImport(file);
  };

  const runImport = async (file, updateExisting) => {
    setPendingImport(null);
    if (!file) return;
    console.log('[locations/import] start', { name: file.name, size: file.size, updateExisting });
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/locations/import?updateExisting=${updateExisting}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('[locations/import] response', res.status, res.data);
      const { totalRows = 0, inserted = 0, updated = 0, errorCount = 0, errors = [] } = res.data || {};

      // Per-error tally to console — same pattern as Asset Registration.
      // Lets the operator see whether all rows share one cause (typo in
      // a parent code, missing required column, etc) without expanding
      // objects in DevTools.
      if (errors.length > 0) {
        const tally = new Map();
        for (const e of errors) {
          const msg = e.errors?.[0] || e.warnings?.[0] || 'unknown';
          tally.set(msg, (tally.get(msg) || 0) + 1);
        }
        const breakdown = [...tally.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([msg, n]) => `  ${n} row(s): ${msg}`)
          .join('\n');
        console.log(`[locations/import] error tally (${errors.length} total):\n${breakdown}`);
      }

      let toastMsg, toastType;
      if (totalRows === 0) {
        toastMsg = 'No rows were imported — the sheet has a header row but no data under it.';
        toastType = 'error';
      } else if (!inserted && !updated && errorCount > 0) {
        const firstErr = errors[0]?.errors?.[0] || errors[0]?.warnings?.[0] || 'unknown';
        toastMsg = `All ${errorCount} row(s) skipped. First error: ${firstErr}. See browser console for the breakdown.`;
        toastType = 'error';
      } else {
        const parts = [];
        if (inserted) parts.push(`${inserted} added`);
        if (updated)  parts.push(`${updated} updated`);
        const summary = parts.length ? parts.join(' · ') : 'no changes';
        toastMsg = `Import complete · ${summary}${errorCount ? ` · ${errorCount} row(s) skipped` : ''}`;
        toastType = errorCount && !inserted && !updated ? 'error' : 'success';
      }
      showToast(toastMsg, toastType);
      await fetchLocations();
    } catch (err) {
      console.error('[locations/import] error', err);
      console.error('[locations/import] error.response?', err.response);
      console.error('[locations/import] error.response?.data?', err.response?.data);
      const data = err.response?.data || {};
      const msg =
        data.error ||
        (err.response ? `HTTP ${err.response.status}` : null) ||
        err.message ||
        'Import failed (open browser console for details)';
      showToast(`Import failed — ${msg}`, 'error');
    }
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Pending bulk-import: ask the operator about duplicates before
          uploading. Replaces the native window.confirm() that looked
          alien against the styled UI. X / overlay click truly aborts;
          Cancel button is "Skip duplicates" (a real action). */}
      <ConfirmModal
        open={!!pendingImport}
        title="Import locations"
        message={
          `Importing "${pendingImport?.name || ''}".\n\n` +
          `When a row matches an existing location by location_code, what should happen?\n\n` +
          `• Update existing — overwrite name, description, parent_id from the file.\n` +
          `• Skip duplicates — leave existing rows untouched; only insert new ones.\n\n` +
          `Click X to cancel the import entirely.`
        }
        confirmLabel="Update existing"
        cancelLabel="Skip duplicates"
        onConfirm={() => runImport(pendingImport, true)}
        onCancel={() => runImport(pendingImport, false)}
        onDismiss={() => setPendingImport(null)}
      />

      {/* Generic confirm — bulk delete uses this. */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        confirmStyle={confirmModal?.confirmStyle}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />

      <div className="page-header">
        <div>
          <div className="page-title"><MdLocationOn /> Location Master</div>
          <div className="page-subtitle">{locations.length} locations · Click any row to see assets &amp; readers there</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            <button
              className={`btn btn-sm ${view === 'flat' ? 'btn-primary' : 'btn-outline'}`}
              style={{ borderRadius: 0, borderRight: '1px solid var(--border)' }}
              onClick={() => setView('flat')}><MdViewList /> Flat</button>
            <button
              className={`btn btn-sm ${view === 'tree' ? 'btn-primary' : 'btn-outline'}`}
              style={{ borderRadius: 0 }}
              onClick={() => setView('tree')}><MdAccountTree /> Tree</button>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => importRef.current.click()}><MdUpload /> Import</button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
          <button className="btn btn-success btn-sm" onClick={handleExport}><MdFileDownload /> Export Excel</button>
        </div>
      </div>

      {/* Add Form */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title"><MdAdd style={{ color: 'var(--accent)' }} /> Add New Location</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-row-3">
            <div className="form-group mb-0">
              <label className="form-label">Location Code *</label>
              <input className="form-control" value={form.location_code}
                onChange={e => { setForm(f => ({ ...f, location_code: e.target.value })); setErrors(er => ({ ...er, location_code: '' })); }}
                placeholder="e.g. WH-B"
                style={{ border: errors.location_code ? '1.5px solid var(--danger)' : undefined }} />
              {errors.location_code && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>{errors.location_code}</div>}
            </div>
            <div className="form-group mb-0">
              <label className="form-label">Location Name *</label>
              <input className="form-control" value={form.location_name}
                onChange={e => { setForm(f => ({ ...f, location_name: e.target.value })); setErrors(er => ({ ...er, location_name: '' })); }}
                placeholder="e.g. Warehouse B"
                style={{ border: errors.location_name ? '1.5px solid var(--danger)' : undefined }} />
              {errors.location_name && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>{errors.location_name}</div>}
            </div>
            <div className="form-group mb-0">
              <label className="form-label">Parent (optional)</label>
              <SearchableSelect className="form-control" value={form.parent_id}
                onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))}>
                <option value="">— Top level —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.location_code} · {l.location_name}</option>)}
              </SearchableSelect>
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Description</label>
            <input className="form-control" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description" />
          </div>
          <div style={{ marginTop: 14 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <MdAdd /> {loading ? 'Adding...' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>

      {/* Locations List */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><MdLocationOn style={{ color: 'var(--accent)' }} /> All Locations</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {selectedIds.size > 0 && (
              <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                <MdDelete /> Delete {selectedIds.size}
              </button>
            )}
            <span className="badge badge-primary">{locations.length} locations</span>
          </div>
        </div>
        {view === 'tree' ? (
          <div style={{ padding: 16 }}>
            {tree.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No locations yet</div>
            ) : (
              <TreeView nodes={tree} onClick={loc => nav(`/locations/${loc.id}`)} onEdit={setEditModal} onDelete={setDeleteModal} />
            )}
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={locations.length > 0 && selectedIds.size === locations.length}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>#</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Parent</th>
                  <th>Description</th>
                  <th>Assets</th>
                  <th>Children</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No locations added yet</td></tr>
                ) : locations.map((loc, i) => {
                  const parent = locations.find(l => l.id === loc.parent_id);
                  return (
                    <tr key={loc.id} style={{ cursor: 'pointer' }}>
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(loc.id)}
                          onChange={() => toggleSelect(loc.id)}
                        />
                      </td>
                      <td onClick={() => nav(`/locations/${loc.id}`)} style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                      <td onClick={() => nav(`/locations/${loc.id}`)}>
                        <span style={{
                          background: 'var(--primary)', color: 'white',
                          padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        }}>{loc.location_code}</span>
                      </td>
                      <td onClick={() => nav(`/locations/${loc.id}`)} style={{ fontWeight: 500, color: 'var(--accent)' }}>{loc.location_name}</td>
                      <td onClick={() => nav(`/locations/${loc.id}`)} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{parent?.location_name || '—'}</td>
                      <td onClick={() => nav(`/locations/${loc.id}`)} style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{loc.description || '—'}</td>
                      <td onClick={() => nav(`/locations/${loc.id}`)}>
                        <span className={`badge ${loc.asset_count > 0 ? 'badge-info' : 'badge-secondary'}`}>{loc.asset_count}</span>
                      </td>
                      <td onClick={() => nav(`/locations/${loc.id}`)}>
                        <span className={`badge ${loc.child_count > 0 ? 'badge-info' : 'badge-secondary'}`}>{loc.child_count}</span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="action-buttons">
                          <button className="btn btn-accent btn-icon btn-sm" title="Edit" onClick={() => setEditModal({ ...loc })}>
                            <MdEdit />
                          </button>
                          <button className="btn btn-danger btn-icon btn-sm" title="Delete" onClick={() => setDeleteModal(loc)}
                            disabled={loc.asset_count > 0 || loc.child_count > 0}>
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdEdit /> Edit Location</div>
              <button className="btn btn-icon" onClick={() => setEditModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Location Code</label>
                <input className="form-control" value={editModal.location_code}
                  onChange={e => setEditModal(m => ({ ...m, location_code: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Location Name</label>
                <input className="form-control" value={editModal.location_name}
                  onChange={e => setEditModal(m => ({ ...m, location_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Parent Location</label>
                <SearchableSelect className="form-control" value={editModal.parent_id || ''}
                  onChange={e => setEditModal(m => ({ ...m, parent_id: e.target.value || null }))}>
                  <option value="">— Top level —</option>
                  {locations.filter(l => l.id !== editModal.id).map(l =>
                    <option key={l.id} value={l.id}>{l.location_code} · {l.location_name}</option>
                  )}
                </SearchableSelect>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" value={editModal.description || ''}
                  onChange={e => setEditModal(m => ({ ...m, description: e.target.value }))} rows={2} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave}><MdSave /> Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: 'var(--danger)' }}>⚠ Delete Location</div>
              <button className="btn btn-icon" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-msg">
                Delete location <span className="confirm-value">"{deleteModal.location_name}"</span>?
                This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}><MdDelete /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tree view (recursive, collapsible) ───────────────────────────────────────
const TreeView = ({ nodes, onClick, onEdit, onDelete, depth = 0 }) => (
  <div>{nodes.map(n => <TreeNode key={n.id} node={n} depth={depth} onClick={onClick} onEdit={onEdit} onDelete={onDelete} />)}</div>
);

const TreeNode = ({ node, depth, onClick, onEdit, onDelete }) => {
  const [open, setOpen] = useState(depth < 2); // open first two levels by default
  const hasChildren = node.children && node.children.length > 0;
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
        marginLeft: depth * 22, borderRadius: 6,
        background: depth === 0 ? '#f8f9fb' : 'transparent',
        borderLeft: depth > 0 ? '2px solid #e0e4eb' : 'none',
        cursor: 'pointer',
      }}
        onClick={() => onClick && onClick(node)}
      >
        <span
          onClick={e => { e.stopPropagation(); if (hasChildren) setOpen(o => !o); }}
          style={{
            width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: hasChildren ? 'var(--accent)' : 'transparent',
            cursor: hasChildren ? 'pointer' : 'default', fontSize: 16,
          }}
        >
          {hasChildren ? (open ? <MdExpandMore /> : <MdChevronRight />) : '•'}
        </span>
        <span style={{
          background: 'var(--primary)', color: 'white',
          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        }}>{node.location_code}</span>
        <span style={{ fontWeight: 500, color: 'var(--accent)' }}>{node.location_name}</span>
        <span className="badge badge-secondary" style={{ marginLeft: 4 }}>{node.asset_count} assets</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-accent btn-icon btn-sm" title="Edit" onClick={() => onEdit({ ...node })}>
            <MdEdit />
          </button>
          <button className="btn btn-danger btn-icon btn-sm" title="Delete" onClick={() => onDelete(node)}
            disabled={node.asset_count > 0 || hasChildren}>
            <MdDelete />
          </button>
        </div>
      </div>
      {hasChildren && open && (
        <TreeView nodes={node.children} onClick={onClick} onEdit={onEdit} onDelete={onDelete} depth={depth + 1} />
      )}
    </div>
  );
};

export default LocationMaster;
